<script setup>
import { computed, ref } from 'vue';

const props = defineProps({
  answers: { type: Object, required: true },
  result: { type: Object, default: null },
  loading: { type: Boolean, default: false }
});

const emit = defineEmits(['edit']);

const showAnswers = ref(false);
const expandedHoldings = ref({});

const recommendation = computed(() => props.result?.recommendation || null);
const primary = computed(() => recommendation.value?.primary || null);
const secondary = computed(() => recommendation.value?.secondary || null);
const whyRecommended = computed(() => recommendation.value?.whyRecommended || []);
const allScores = computed(() => props.result?.allScores || {});

const primaryVehicles = computed(() => primary.value?.vehicles || []);
const secondaryVehicles = computed(() => secondary.value?.vehicles || []);

const getRouteColor = (route) => {
  const colors = {
    'FEEDER': '#8b5cf6',
    'GIFT': '#3b82f6',
    'DIRECT': '#ef4444'
  };
  return colors[route] || '#6b7280';
};

const getRouteGradient = (route) => {
  const gradients = {
    'FEEDER': 'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)',
    'GIFT': 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
    'DIRECT': 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)'
  };
  return gradients[route] || 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)';
};

const formatCurrency = (value) => {
  if (!value && value !== 0) return 'N/A';
  return `₹${value.toLocaleString('en-IN')}`;
};

const toggleHoldings = (routeId) => {
  expandedHoldings.value[routeId] = !expandedHoldings.value[routeId];
};
</script>

<template>
  <div class="result-wrapper">
    <div class="gi-result-header">
      <div>
        <h2 class="gi-title">Your recommended investment route</h2>
        <p class="gi-subtitle">
          Based on your profile, we've identified the best global investing approach for you.
        </p>
      </div>
      <button class="gi-link-btn" type="button" @click="emit('edit')" :disabled="loading">
        ← Change answers
      </button>
    </div>

    <div v-if="loading" class="gi-loading-box">
      <div class="gi-spinner" />
      <p>Analyzing your preferences...</p>
    </div>

    <template v-else-if="recommendation">
      <!-- Primary Recommendation -->
      <div v-if="primary" class="route-section">
        <div class="route-header">
          <div class="route-badge" :style="{ background: getRouteColor(primary.route) }">
            Primary Route
          </div>
          <div class="route-score">Score: {{ primary.score }}/20</div>
        </div>
        <h3 class="route-title">{{ primary.label }}</h3>
        <div class="confidence-bar">
          <div class="confidence-label">Confidence: {{ Math.round((recommendation.confidence || 0) * 100) }}%</div>
          <div class="confidence-track">
            <div class="confidence-fill" :style="{ width: (recommendation.confidence * 100) + '%' }"></div>
          </div>
        </div>

        <!-- Why Recommended -->
        <div class="why-section">
          <h4 class="why-title">Why this route?</h4>
          <ul class="why-list">
            <li v-for="(reason, idx) in whyRecommended" :key="idx">{{ reason }}</li>
          </ul>
        </div>

        <!-- Primary Vehicles -->
        <div v-if="primaryVehicles.length > 0" class="vehicles-section">
          <h4 class="vehicles-title">Recommended Funds ({{ primaryVehicles.length }})</h4>
          <div class="vehicles-grid">
            <template v-for="vehicle in primaryVehicles" :key="vehicle.id">
              <div class="vehicle-card" :style="{ background: getRouteGradient(vehicle.route) }">
                <h5 class="vehicle-name">{{ vehicle.name }}</h5>
                <div class="vehicle-meta">
                  <span class="meta-badge" :class="{ active: vehicle.sip }">
                    SIP: {{ vehicle.sip ? '✓' : '✗' }}
                  </span>
                  <span class="meta-badge" :class="{ active: vehicle.lumpsum }">
                    Lumpsum: {{ vehicle.lumpsum ? '✓' : '✗' }}
                  </span>
                </div>
                <div class="vehicle-stats">
                  <div class="stat-row">
                    <span class="stat-label">Currency:</span>
                    <span class="stat-value">{{ vehicle.currency }}</span>
                  </div>
                  <div class="stat-row" v-if="vehicle.attributes?.expenseRatioPct">
                    <span class="stat-label">Expense Ratio:</span>
                    <span class="stat-value">{{ vehicle.attributes.expenseRatioPct }}%</span>
                  </div>
                  <div class="stat-row" v-if="vehicle.attributes?.return3Y_Pct">
                    <span class="stat-label">3Y Return:</span>
                    <span class="stat-value">{{ vehicle.attributes.return3Y_Pct }}%</span>
                  </div>
                  <div class="stat-row" v-if="vehicle.attributes?.minAmount">
                    <span class="stat-label">Min Amount:</span>
                    <span class="stat-value">{{ formatCurrency(vehicle.attributes.minAmount) }}</span>
                  </div>
                </div>
                <p v-if="vehicle.attributes?.notes" class="vehicle-notes">{{ vehicle.attributes.notes }}</p>
              </div>

              <!-- Top Holdings Accordion - Spans both columns -->
              <div v-if="vehicle.attributes?.topHoldings && vehicle.attributes.topHoldings.length > 0" class="holdings-wrapper">
                <div class="holdings-section" :class="'holdings-' + vehicle.route.toLowerCase()">
                  <button
                    class="holdings-toggle"
                    :class="'holdings-toggle-' + vehicle.route.toLowerCase()"
                    @click="toggleHoldings(vehicle.id)"
                    :aria-expanded="expandedHoldings[vehicle.id] ? 'true' : 'false'"
                  >
                    <span class="holdings-header">
                      <i class="pi pi-chart-pie"></i>
                      {{ vehicle.name }} - Top Holdings ({{ vehicle.attributes.topHoldings.length }})
                    </span>
                    <i :class="['pi', expandedHoldings[vehicle.id] ? 'pi-chevron-up' : 'pi-chevron-down']"></i>
                  </button>

                  <div v-show="expandedHoldings[vehicle.id]" class="holdings-content">
                    <div class="holdings-grid">
                      <div
                        v-for="(holding, idx) in vehicle.attributes.topHoldings"
                        :key="idx"
                        class="holding-item"
                      >
                        <div class="holding-name">{{ holding.security }}</div>
                        <div class="holding-details">
                          <span v-if="holding.sector" class="holding-sector">{{ holding.sector }}</span>
                          <span v-if="holding.pct" class="holding-weight">{{ holding.pct }}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Secondary Recommendation -->
      <div v-if="secondary" class="route-section secondary">
        <div class="route-header">
          <div class="route-badge secondary-badge" :style="{ background: getRouteColor(secondary.route) }">
            Alternative Route
          </div>
          <div class="route-score">Score: {{ secondary.score }}/20</div>
        </div>
        <h3 class="route-title">{{ secondary.label }}</h3>
        <p class="route-note">Consider this as an alternative approach if the primary route doesn't fully meet your needs.</p>

        <!-- Secondary Vehicles -->
        <div v-if="secondaryVehicles.length > 0" class="vehicles-section">
          <h4 class="vehicles-title">Alternative Funds ({{ secondaryVehicles.length }})</h4>
          <div class="vehicles-grid">
            <template v-for="vehicle in secondaryVehicles" :key="vehicle.id">
              <div class="vehicle-card" :style="{ background: getRouteGradient(vehicle.route) }">
                <h5 class="vehicle-name">{{ vehicle.name }}</h5>
                <div class="vehicle-meta">
                  <span class="meta-badge" :class="{ active: vehicle.sip }">
                    SIP: {{ vehicle.sip ? '✓' : '✗' }}
                  </span>
                  <span class="meta-badge" :class="{ active: vehicle.lumpsum }">
                    Lumpsum: {{ vehicle.lumpsum ? '✓' : '✗' }}
                  </span>
                </div>
                <div class="vehicle-stats">
                  <div class="stat-row">
                    <span class="stat-label">Currency:</span>
                    <span class="stat-value">{{ vehicle.currency }}</span>
                  </div>
                  <div class="stat-row" v-if="vehicle.attributes?.expenseRatioPct">
                    <span class="stat-label">Expense Ratio:</span>
                    <span class="stat-value">{{ vehicle.attributes.expenseRatioPct }}%</span>
                  </div>
                  <div class="stat-row" v-if="vehicle.attributes?.return3Y_Pct">
                    <span class="stat-label">3Y Return:</span>
                    <span class="stat-value">{{ vehicle.attributes.return3Y_Pct }}%</span>
                  </div>
                  <div class="stat-row" v-if="vehicle.attributes?.minAmount">
                    <span class="stat-label">Min Amount:</span>
                    <span class="stat-value">{{ formatCurrency(vehicle.attributes.minAmount) }}</span>
                  </div>
                </div>
                <p v-if="vehicle.attributes?.notes" class="vehicle-notes">{{ vehicle.attributes.notes }}</p>
              </div>

              <!-- Top Holdings Accordion - Spans both columns -->
              <div v-if="vehicle.attributes?.topHoldings && vehicle.attributes.topHoldings.length > 0" class="holdings-wrapper">
                <div class="holdings-section">
                  <button
                    class="holdings-toggle"
                    @click="toggleHoldings(vehicle.id)"
                    :aria-expanded="expandedHoldings[vehicle.id] ? 'true' : 'false'"
                  >
                    <span class="holdings-header">
                      <i class="pi pi-chart-pie"></i>
                      {{ vehicle.name }} - Top Holdings ({{ vehicle.attributes.topHoldings.length }})
                    </span>
                    <i :class="['pi', expandedHoldings[vehicle.id] ? 'pi-chevron-up' : 'pi-chevron-down']"></i>
                  </button>

                  <div v-show="expandedHoldings[vehicle.id]" class="holdings-content">
                    <div class="holdings-grid">
                      <div
                        v-for="(holding, idx) in vehicle.attributes.topHoldings"
                        :key="idx"
                        class="holding-item"
                      >
                        <div class="holding-name">{{ holding.security }}</div>
                        <div class="holding-details">
                          <span v-if="holding.sector" class="holding-sector">{{ holding.sector }}</span>
                          <span v-if="holding.pct" class="holding-weight">{{ holding.pct }}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- All Scores Comparison -->
      <div class="scores-section">
        <h4 class="scores-title">Route Comparison</h4>
        <div class="scores-grid">
          <div v-for="(score, route) in allScores" :key="route" class="score-item">
            <div class="score-label">{{ route }}</div>
            <div class="score-bar-wrap">
              <div class="score-bar" :style="{ width: (Math.max(0, score) / 20 * 100) + '%', background: getRouteColor(route) }"></div>
            </div>
            <div class="score-value">{{ score }}/20</div>
          </div>
        </div>
      </div>

      <!-- Disclaimer -->
      <div class="disclaimer">
        <i class="pi pi-info-circle"></i>
        <span>{{ props.result?.disclaimer || 'Suitability-based guidance only. Final decision should consider product documents, costs, taxation and individual suitability.' }}</span>
      </div>
    </template>
  </div>
</template>

<style scoped>
.gi-result-root {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.gi-result-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.gi-title {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
  color: #1f2937;
}

.gi-subtitle {
  margin: 4px 0 0 0;
  font-size: 0.85rem;
  color: #6b7280;
}

.gi-link-btn {
  border: none;
  background: transparent;
  color: #3b82f6;
  font-size: 0.8rem;
  cursor: pointer;
  font-weight: 600;
}

.gi-loading-box {
  border-radius: 8px;
  border: 1px solid #e9ecef;
  padding: 40px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  background: #f9fafb;
}

.gi-spinner {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 3px solid #e9ecef;
  border-top-color: #3b82f6;
  animation: spin 1s linear infinite;
}

.route-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  border: 2px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.route-card.primary {
  border-color: #3b82f6;
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.15);
}

.route-card.secondary {
  border-color: #d1d5db;
}

.route-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.route-badge {
  padding: 4px 12px;
  border-radius: 999px;
  color: white;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
}

.secondary-badge {
  opacity: 0.8;
}

.route-score {
  font-size: 0.9rem;
  font-weight: 700;
  color: #374151;
}

.route-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 12px 0;
}

.route-note {
  font-size: 0.85rem;
  color: #6b7280;
  margin: 0;
}

.confidence-bar {
  margin: 12px 0;
}

.confidence-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
}

.confidence-track {
  height: 8px;
  background: #e5e7eb;
  border-radius: 999px;
  overflow: hidden;
}

.confidence-fill {
  height: 100%;
  background: linear-gradient(90deg, #10b981, #059669);
  transition: width 0.5s ease;
}

.why-section {
  margin: 16px 0;
  padding: 14px;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border-radius: 8px;
}

.why-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: #1e40af;
  margin: 0 0 8px 0;
}

.why-list {
  margin: 0;
  padding-left: 20px;
  list-style: disc;
}

.why-list li {
  font-size: 0.85rem;
  color: #1e3a8a;
  line-height: 1.6;
  margin: 4px 0;
}

.vehicles-section {
  margin-top: 20px;
}

.vehicles-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 12px 0;
}

.vehicles-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.vehicle-card {
  padding: 16px;
  border-radius: 10px;
  border: 2px solid #e5e7eb;
}

.vehicle-name {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 10px 0;
}

.vehicle-meta {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.meta-badge {
  padding: 3px 8px;
  background: #f3f4f6;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  color: #6b7280;
}

.meta-badge.active {
  background: #d1fae5;
  color: #065f46;
}

.vehicle-stats {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.stat-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
}

.stat-label {
  color: #6b7280;
}

.stat-value {
  font-weight: 600;
  color: #374151;
}

.vehicle-notes {
  margin: 12px 0 0 0;
  font-size: 0.75rem;
  color: #6b7280;
  font-style: italic;
  padding: 8px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 4px;
}

.scores-section {
  background: #f9fafb;
  padding: 16px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
}

.scores-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 12px 0;
}

.scores-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.score-item {
  display: grid;
  grid-template-columns: 80px 1fr 60px;
  gap: 12px;
  align-items: center;
}

.score-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #374151;
}

.score-bar-wrap {
  height: 8px;
  background: #e5e7eb;
  border-radius: 999px;
  overflow: hidden;
}

.score-bar {
  height: 100%;
  transition: width 0.5s ease;
}

.score-value {
  font-size: 0.8rem;
  font-weight: 700;
  color: #374151;
  text-align: right;
}

.disclaimer {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-left: 3px solid #f59e0b;
  border-radius: 6px;
  font-size: 0.8rem;
  color: #78350f;
  line-height: 1.5;
}

.disclaimer i {
  color: #f59e0b;
  font-size: 1rem;
  flex-shrink: 0;
  margin-top: 2px;
}

/* Holdings Accordion - Wrapper spans full width */
.holdings-wrapper {
  grid-column: 1 / -1;
  margin-top: -8px;
  margin-bottom: 8px;
  padding: 0 12px;
}

.holdings-section {
  border-top: 1px solid #e5e7eb;
  padding-top: 12px;
  border-radius: 0 0 8px 8px;
  padding: 12px;
  margin: 0 -12px;
}

/* FEEDER route - Purple theme */
.holdings-feeder {
  background: linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%);
}

.holdings-toggle-feeder {
  background: linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%);
  border: 1px solid #e9d5ff;
  color: #6b21a8;
}

.holdings-toggle-feeder:hover {
  background: linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%);
  border-color: #d8b4fe;
}

.holdings-toggle-feeder .holdings-header i {
  color: #7c3aed;
}

.holdings-toggle-feeder > i {
  color: #7c3aed;
}

/* GIFT route - Blue theme */
.holdings-gift {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
}

.holdings-toggle-gift {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid #bae6fd;
  color: #0c4a6e;
}

.holdings-toggle-gift:hover {
  background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%);
  border-color: #7dd3fc;
}

.holdings-toggle-gift .holdings-header i {
  color: #0369a1;
}

.holdings-toggle-gift > i {
  color: #0369a1;
}

/* DIRECT route - Red/Pink theme */
.holdings-direct {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
}

.holdings-toggle-direct {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border: 1px solid #fecaca;
  color: #7f1d1d;
}

.holdings-toggle-direct:hover {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  border-color: #fca5a5;
}

.holdings-toggle-direct .holdings-header i {
  color: #dc2626;
}

.holdings-toggle-direct > i {
  color: #dc2626;
}

.holdings-toggle {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
}

.holdings-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
}

.holdings-header i {
  font-size: 1rem;
  color: #0369a1;
}

.holdings-toggle > i {
  font-size: 0.9rem;
  color: #0369a1;
  transition: transform 0.3s ease;
}

.holdings-content {
  margin-top: 12px;
  padding: 16px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.holdings-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.holding-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
}

.holding-item:hover {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border-color: #93c5fd;
  transform: translateX(4px);
}

.holding-name {
  font-weight: 600;
  font-size: 0.85rem;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.holding-details {
  display: flex;
  align-items: center;
  gap: 8px;
}

.holding-ticker {
  padding: 3px 8px;
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1e40af;
  border-radius: 4px;
  font-weight: 600;
  font-family: 'Courier New', monospace;
  font-size: 0.75rem;
}

.holding-sector {
  padding: 4px 10px;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #78350f;
  border-radius: 4px;
  font-weight: 500;
  font-size: 0.75rem;
  white-space: nowrap;
  flex-shrink: 0;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.holding-weight {
  padding: 4px 10px;
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #065f46;
  border-radius: 4px;
  font-weight: 700;
  font-size: 0.8rem;
  min-width: 60px;
  text-align: center;
  flex-shrink: 0;
}

@media (max-width: 968px) {
  .vehicles-grid {
    grid-template-columns: 1fr;
  }

  .holdings-wrapper {
    grid-column: 1;
  }
}

@media (max-width: 768px) {
  .gi-result-header {
    flex-direction: column;
  }

  .holdings-wrapper {
    margin: -8px 0 8px 0;
    padding: 0;
  }

  .holdings-section {
    margin: 0;
  }

  .holdings-header {
    font-size: 0.75rem;
  }

  .holding-item {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .holding-name {
    font-size: 0.8rem;
  }

  .holding-details {
    justify-content: flex-start;
  }

  .holding-sector,
  .holding-weight {
    font-size: 0.7rem;
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
